/**
 * Physics for the brick pile, independent of canvas/DOM so it can be tested.
 *
 * Bricks fall into columns and settle on a per-column height map, so the pile
 * grows with natural gaps and overhangs. When every column reaches the top the
 * pile fades away and starts over. A brick can be grabbed, dragged and thrown.
 */

export interface Brick {
  /** Leftmost column the brick occupies (integer). */
  c: number;
  /** Visual column position; eases toward `c` once the brick stops moving sideways. */
  fx: number;
  /** Width in studs (2–4). */
  w: number;
  /** Top edge of the front face, in px from the top of the canvas. */
  y: number;
  vx: number;
  vy: number;
  color: string;
}

export interface SimOptions {
  width: number;
  height: number;
  /** Stud size in px. */
  unit: number;
  colors: string[];
  /** Spawn rate multiplier. */
  rate: number;
  random?: () => number;
}

interface Sample {
  x: number;
  y: number;
  t: number;
}

interface Drag {
  b: Brick;
  ox: number;
  oy: number;
  samples: Sample[];
}

export interface Point {
  x: number;
  y: number;
}

const GRAVITY = 300;
const TERMINAL_VY = 260;
/** How quickly a thrown brick slows back down to terminal speed (px/s²). */
const OVERSPEED_DRAG = 900;
/** Horizontal velocity decay per second (exponential). */
const AIR_DRAG = 1.6;
const MAX_THROW = 1400;
/** Pointer samples older than this (ms) don't count towards the throw. */
const THROW_WINDOW = 90;
const FADE_SPEED = 0.8;
const PREFILL_SETTLED = 28;
const PREFILL_FALLING = 6;

export class BrickSim {
  W = 0;
  H = 0;
  u = 0;
  bh = 0;
  dx = 0;
  dy = 0;
  cols = 0;
  off = 0;
  /** False until the canvas is big enough to hold the widest (4-stud) brick. */
  ready = false;

  falling: Brick[] = [];
  settled: Brick[] = [];
  heights: number[] = [];
  fade = 1;
  clearing = false;
  drag: Drag | null = null;

  private acc = 0;
  private colors: string[];
  private rate: number;
  private baseUnit: number;
  private rand: () => number;

  constructor(opts: SimOptions) {
    this.colors = opts.colors;
    this.rate = opts.rate;
    this.baseUnit = opts.unit;
    this.rand = opts.random ?? Math.random;
    this.resize(opts.width, opts.height);
  }

  resize(width: number, height: number) {
    this.W = width;
    this.H = height;
    this.u = width < 500 ? this.baseUnit * 0.8 : this.baseUnit;
    this.bh = this.u * 1.1;
    this.dx = this.u * 0.6;
    this.dy = this.u * 0.42;
    this.cols = Math.floor((width - this.dx) / this.u);
    this.ready = this.cols >= 4 && height > 0;
    this.settled = [];
    this.falling = [];
    this.drag = null;
    this.fade = 1;
    this.clearing = false;
    this.acc = 0;
    if (!this.ready) return;
    this.off = (width - this.dx - this.cols * this.u) / 2;
    this.heights = new Array(this.cols).fill(0);
    for (let i = 0; i < PREFILL_SETTLED; i++) this.spawn(true);
    for (let i = 0; i < PREFILL_FALLING; i++) this.spawn(false, this.rand() * height * 0.6);
  }

  /** Share of the screen covered by the pile, 0–1. */
  get fill() {
    let sum = 0;
    for (const h of this.heights) sum += Math.min(h, this.H);
    return sum / (this.cols * this.H);
  }

  /** y at which a brick spanning columns [c, c+w) would rest. */
  topAt(c: number, w: number) {
    let m = 0;
    for (let i = c; i < c + w; i++) m = Math.max(m, this.heights[i]);
    return this.H - m - this.bh;
  }

  top(b: Brick) {
    return this.topAt(b.c, b.w);
  }

  /**
   * y at which a brick would come to rest falling straight down from where it
   * is now. Unlike the skyline this sees gaps under overhangs.
   */
  floorFor(b: Brick, c = b.c) {
    let f = this.H - this.bh;
    for (const o of this.settled) {
      if (o !== b && o.c < c + b.w && c < o.c + o.w && o.y >= b.y - 0.5) f = Math.min(f, o.y - this.bh);
    }
    return f;
  }

  /** Left edge of the brick's front face in px. */
  xOf(b: Brick) {
    return this.off + b.fx * this.u;
  }

  /** A spot is open if a brick dropped there would come to rest mostly on screen. */
  private open(c: number, w: number) {
    return this.topAt(c, w) > -this.bh * 0.5;
  }

  private openSpots(w: number) {
    const spots: number[] = [];
    for (let c = 0; c + w <= this.cols; c++) if (this.open(c, w)) spots.push(c);
    return spots;
  }

  spawn(instant: boolean, y?: number) {
    // Only drop where the brick will end up on screen, narrowing it to fit the
    // last gaps, so bricks never pile up off-screen.
    let w = 2 + Math.floor(this.rand() * 3);
    let open = this.openSpots(w);
    while (!open.length && w > 2) open = this.openSpots(--w);
    if (!open.length) return;
    // Prefer the lowest of a few random spots so the pile grows fairly evenly.
    let c = open[0], best = -Infinity;
    for (let k = 0; k < 4; k++) {
      const cc = open[Math.floor(this.rand() * open.length)];
      const tp = this.topAt(cc, w);
      if (tp > best) { best = tp; c = cc; }
    }
    const b: Brick = {
      c,
      fx: c,
      w,
      y: y ?? -this.bh * 2,
      vx: 0,
      vy: 20 + this.rand() * 30,
      color: this.colors[Math.floor(this.rand() * this.colors.length)],
    };
    b.y = Math.min(b.y, this.top(b));
    if (instant) this.land(b);
    else {
      this.liftClear(b);
      this.falling.push(b);
    }
  }

  /** Move a brick that's about to join the falling ones up until it overlaps none of them. */
  private liftClear(b: Brick) {
    for (let moved = true, n = 0; moved && n < 100; n++) {
      moved = false;
      for (const o of this.falling) {
        if (this.overlaps(o, b) && Math.abs(o.y - b.y) < this.bh - 0.5) { b.y = o.y - this.bh; moved = true; }
      }
    }
  }

  land(b: Brick) {
    b.y = this.floorFor(b);
    b.vy = 0;
    b.vx = 0;
    for (let i = b.c; i < b.c + b.w; i++) this.heights[i] = Math.max(this.heights[i], this.H - b.y);
    this.settled.push(b);
    if (!this.clearing && !this.openSpots(2).length) this.clearing = true;
  }

  /** Rebuild the height map after a brick was pulled out; unsupported bricks start falling. */
  private restack() {
    const list = this.settled.sort((a, b) => b.y - a.y);
    this.settled = [];
    this.heights.fill(0);
    for (const b of list) {
      if (b.y >= this.floorFor(b) - 0.5) this.land(b);
      else { b.vy = 0; this.falling.push(b); }
    }
  }

  private overlaps(a: Brick, b: Brick) {
    return a.c < b.c + b.w && b.c < a.c + a.w;
  }

  step(dt: number) {
    if (!this.ready) return;
    if (this.clearing) {
      this.fade -= dt * FADE_SPEED;
      if (this.fade <= 0) {
        this.settled = [];
        this.falling = [];
        this.heights.fill(0);
        this.fade = 1;
        this.clearing = false;
      }
    } else {
      // Speed up a little as the pile grows, but keep the sky readable.
      const fill = this.fill;
      this.acc += dt * this.rate * (this.W / 600) * (this.H / 720) * (2 + fill * 2.5);
      const maxFalling = Math.max(4, Math.round(this.cols * 0.4));
      while (this.acc > 1) {
        this.acc -= 1;
        if (this.falling.length < maxFalling) this.spawn(false);
      }
    }
    this.moveFalling(dt);
  }

  private moveFalling(dt: number) {
    const { bh, H } = this;
    // Lowest first, so a brick can rest on one that's falling below it.
    const queue = this.falling.sort((a, b) => b.y - a.y);
    const moved: Brick[] = [];
    const landed = new Set<Brick>();

    for (const b of queue) {
      this.moveSideways(b, dt);

      if (b.vy < TERMINAL_VY) b.vy = Math.min(b.vy + GRAVITY * dt, TERMINAL_VY);
      else b.vy = Math.max(TERMINAL_VY, b.vy - OVERSPEED_DRAG * dt);
      let ny = b.y + b.vy * dt;

      if (b.vy < 0) {
        // Thrown upward: don't pass through bricks above (e.g. an overhang).
        let ceil = -H;
        for (const o of this.settled.concat(queue)) {
          if (o !== b && this.overlaps(o, b) && o.y + bh <= b.y + 0.5) ceil = Math.max(ceil, o.y + bh);
        }
        if (ny <= ceil) { ny = ceil; b.vy = 0; }
      } else {
        const ground = this.floorFor(b);
        let floor = ground;
        let under: Brick | null = null;
        for (const o of moved) {
          if (this.overlaps(o, b) && o.y - bh < floor) { floor = o.y - bh; under = o; }
        }
        if (ny >= floor) {
          if (!under) {
            // A brick that would come to rest entirely above the screen is just dropped.
            if (floor + bh > 0) this.land(b);
            landed.add(b);
            continue;
          }
          ny = floor;
          b.vy = Math.min(b.vy, under.vy);
        }
      }
      b.y = ny;
      moved.push(b);
    }
    if (landed.size) this.falling = this.falling.filter((b) => !landed.has(b));
    // Bricks that landed mid-slide ease into their column.
    for (const b of this.settled) if (b.fx !== b.c) this.easeToColumn(b, dt);
  }

  private easeToColumn(b: Brick, dt: number) {
    const d = b.c - b.fx;
    b.fx = Math.abs(d) < 0.01 ? b.c : b.fx + d * Math.min(1, dt * 18);
  }

  private moveSideways(b: Brick, dt: number) {
    if (b.vx === 0) {
      if (b.fx !== b.c) this.easeToColumn(b, dt);
      return;
    }
    b.vx *= Math.exp(-AIR_DRAG * dt);
    if (Math.abs(b.vx) < 8) b.vx = 0;
    let nfx = b.fx + (b.vx * dt) / this.u;
    const max = this.cols - b.w;
    if (nfx < 0) { nfx = 0; b.vx = -b.vx * 0.5; }
    else if (nfx > max) { nfx = max; b.vx = -b.vx * 0.5; }
    const nc = Math.round(nfx);
    if (nc !== b.c) {
      // Bump off the side of the pile instead of teleporting on top of it.
      const hits = (o: Brick) => o !== b && o.c < nc + b.w && nc < o.c + o.w && Math.abs(o.y - b.y) < this.bh - 0.5;
      const blocked = this.settled.some(hits) || this.falling.some(hits);
      if (blocked) {
        b.vx = -b.vx * 0.3;
        b.fx = b.c + (nc > b.c ? 0.49 : -0.49);
        return;
      }
      b.c = nc;
    }
    b.fx = nfx;
  }

  /**
   * Bricks in back-to-front paint order: row by row from the bottom, left to
   * right within a row (so a brick's side face is covered by its right
   * neighbour), and bottom-up within a stack. Rows are bucketed so bricks
   * falling past the pile at in-between heights still order correctly.
   */
  order() {
    const row = (b: Brick) => Math.round((this.H - b.y) / this.bh);
    return this.settled
      .concat(this.falling)
      .sort((a, b) => row(a) - row(b) || (this.overlaps(a, b) ? b.y - a.y : a.fx - b.fx));
  }

  hit(b: Brick, p: Point) {
    const x = this.xOf(b);
    return p.x >= x && p.x <= x + b.w * this.u + this.dx && p.y >= b.y - this.dy - this.u * 0.3 && p.y <= b.y + this.bh;
  }

  grab(p: Point, now: number) {
    if (!this.ready || this.drag) return false;
    const b = this.order().reverse().find((k) => this.hit(k, p));
    if (!b) return false;
    const fi = this.falling.indexOf(b);
    if (fi >= 0) this.falling.splice(fi, 1);
    else {
      this.settled.splice(this.settled.indexOf(b), 1);
      this.restack();
    }
    b.vx = 0;
    b.vy = 0;
    this.drag = { b, ox: p.x - this.xOf(b), oy: p.y - b.y, samples: [{ ...p, t: now }] };
    return true;
  }

  moveDrag(p: Point, now: number) {
    const d = this.drag;
    if (!d) return;
    const b = d.b;
    b.fx = Math.max(0, Math.min(this.cols - b.w, (p.x - d.ox - this.off) / this.u));
    b.c = Math.round(b.fx);
    // Keep the brick above the pile under every column it visually covers.
    const lo = Math.floor(b.fx), hi = Math.ceil(b.fx);
    b.y = Math.min(p.y - d.oy, this.topAt(lo, hi - lo + b.w));
    d.samples.push({ ...p, t: now });
    while (d.samples.length > 2 && now - d.samples[1].t > THROW_WINDOW) d.samples.shift();
  }

  release(now: number) {
    const d = this.drag;
    if (!d) return;
    const b = d.b;
    const recent = d.samples.filter((s) => now - s.t <= THROW_WINDOW);
    b.vx = 0;
    b.vy = 0;
    if (recent.length >= 2) {
      const a = recent[0], z = recent[recent.length - 1];
      const span = Math.max(16, z.t - a.t) / 1000;
      const clamp = (v: number) => Math.max(-MAX_THROW, Math.min(MAX_THROW, v));
      b.vx = clamp((z.x - a.x) / span);
      b.vy = clamp((z.y - a.y) / span);
    }
    // While held, other bricks may have fallen through its spot; lift it clear
    // of the pile and of anything falling before letting go.
    b.c = Math.round(b.fx);
    b.y = Math.min(b.y, this.top(b));
    this.liftClear(b);
    this.falling.push(b);
    this.drag = null;
  }
}
